/* ReplyComposer — matches Figma "Input Fields / Type=Free text"
 * (fileKey QkVMIm6TUdblppfqYlUtai, nodes 1135:6452 / 1135:6590 light,
 * 1135:6550 / 1135:6709 dark).
 *
 * Two states:
 *   .collapsed — idle, empty, unfocused  (~36px min-height, bottom row hidden)
 *   .expanded  — focused OR non-empty    (grows to content, bottom row visible,
 *                                         active border + focus ring)
 *
 * Height transition (150ms ease) is on the .inner element so only the
 * collapse/expand animation runs — individual keystroke auto-resize is done
 * via JS directly on the textarea element and bypasses the transition.
 *
 * Theme switching via [data-theme="dark"] parent selector — NOT a prop. */

/* ─── Outer wrapper ──────────────────────────────────────────────── */

.composer {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-4);
  font-family: var(--font-family);
  font-size: var(--text-size-s);
  line-height: var(--text-lh-s);
  color: var(--text-primary);
  width: 100%;
}

/* ─── Label ──────────────────────────────────────────────────────── */

.label {
  font: var(--text-body-medium);
  color: var(--text-primary);
}

/* Visually hidden but still reachable for screen readers.
 * Standard sr-only pattern (WCAG-friendly, won't be picked up by layout). */
.labelVisuallyHidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* ─── Inner container (the visible bordered box) ─────────────────── */

.inner {
  display: flex;
  flex-direction: column;
  gap: 0;
  background: var(--bg-primary);
  border: 1px solid var(--stroke-neutral);
  border-radius: var(--radius-10);
  padding: 7px 8px 6px;
  box-sizing: border-box;
  /* Height expansion uses a restrained overshoot (peaks roughly 2-3px
   * past target, then settles) over 280ms — gives the expand a sense
   * of weight and intent without feeling toy-like. Border + shadow
   * transitions stay snappy at 150ms because focus-state feedback
   * needs to be instant. Per-keystroke textarea height changes bypass
   * all of this — they happen directly on textarea.style.height via
   * JS so typing doesn't animate. */
  transition:
    border-color var(--motion-duration-fast) ease,
    box-shadow var(--motion-duration-fast) ease,
    background var(--motion-duration-fast) ease,
    min-height 280ms cubic-bezier(0.34, 1.25, 0.64, 1);
  overflow: hidden;
}

/* ─── Collapsed state ────────────────────────────────────────────── */

/* Minimum height: padding-top(7) + line-height(20) + padding-bottom(6) = 33px.
 * We round up to 36px to give breathing room and match the Figma idle height. */
.collapsed .inner {
  min-height: 36px;
}

/* ─── Expanded state ─────────────────────────────────────────────── */

.expanded .inner {
  border-color: var(--stroke-input-active);
  /* Focus ring derives from --stroke-input-active via color-mix so a
   * future accent color swap is a single-token change. 20% alpha is
   * the convention shared with Checkbox and Toggle. */
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--stroke-input-active) 20%, transparent);
  /* Matches the Figma conversation-context focused Free text height
   * (Component46 Reply active inside the conversation variant = 97px). */
  min-height: 97px;
}

/* Dark theme: expanded state swaps background to --bg-secondary */
[data-theme="dark"] .expanded .inner {
  background: var(--bg-secondary);
}

/* ─── Disabled state ─────────────────────────────────────────────── */

.disabled .inner {
  background: var(--bg-secondary);
  cursor: not-allowed;
}

.disabled .textarea {
  cursor: not-allowed;
  color: var(--text-tertiary);
}

/* ─── Textarea ───────────────────────────────────────────────────── */

.textarea {
  flex: 1;
  width: 100%;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  color: var(--text-primary);
  font-family: var(--font-family);
  font-size: var(--text-size-s);
  line-height: var(--text-lh-s);
  /* rows=1 keeps the native intrinsic height at one line.
   * JS sets height=auto then height=scrollHeight on input so it
   * auto-resizes without CSS transition on every keystroke. */
  resize: none;
  padding: 0;
  margin: 0;
  box-sizing: border-box;
  overflow: hidden; /* overflowY toggled by JS once content exceeds maxHeight */
  /* Prevent the textarea from collapsing to 0 in the bottom direction
   * when it has no content — keeps the composer at min-height without
   * flex stretching the textarea weirdly. */
  min-height: 20px; /* one line-height */
}

.textarea::placeholder {
  color: var(--text-placeholder);
  font-weight: var(--text-weight-regular);
}

/* ─── Bottom row ─────────────────────────────────────────────────── */

.bottomRow {
  align-items: center;
  justify-content: space-between;
  margin-top: 4px;
  /* Removed from layout flow in collapsed state — using opacity alone left
   * the row occupying ~28px (margin + icon button height) and inflated the
   * collapsed composer past the 36px Figma target. display:none drops it
   * from flow entirely so the inner collapses to textarea + padding only. */
  display: none;
}

/* Expanded state: the row re-enters flow (display:flex) and runs a
 * short fade-in with a 4px upward slide. The 140ms delay lets the
 * inner container's height expansion lead — the row feels like it
 * "swims up" into the freshly-expanded composer, rather than
 * appearing in sync with the container growing around it. `both` on
 * the animation keeps the final state so the row stays visible after
 * the animation completes. */
.expanded .bottomRow {
  display: flex;
  animation: bottomRowFadeIn 180ms ease-out 140ms both;
}

@keyframes bottomRowFadeIn {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Respect the user's motion preference — collapse every transition
 * and animation in this component to effectively-instant. WCAG 2.3.3
 * / prefers-reduced-motion guidance: animations shouldn't be the
 * medium of information, just flourish, so flattening them is safe. */
@media (prefers-reduced-motion: reduce) {
  .inner,
  .expanded .bottomRow {
    transition-duration: 0.01ms !important;
    animation-duration: 0.01ms !important;
    animation-delay: 0ms !important;
  }
}

/* Spacer — fills the left slot when emoji button is absent, keeping the
 * send button pinned to the right. Zero width but present in flow. */
.spacer {
  flex: 1;
}

/* ─── Icon buttons (emoji + send) ────────────────────────────────── */

.iconBtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: var(--spacing-4);
  border: none;
  border-radius: var(--radius-4);
  background: transparent;
  cursor: pointer;
  color: var(--icon-muted);
  box-sizing: border-box;
  transition:
    background 0.1s ease,
    color 0.1s ease;
  flex-shrink: 0;
}

.iconBtn:hover:not(:disabled) {
  background: var(--icon-bg-hover);
  color: var(--icon-default);
}

.iconBtn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 1px var(--text-primary);
}

.iconBtn:disabled {
  cursor: not-allowed;
  opacity: 0.4;
}

/* Send button: brand color when active (not disabled). */
.sendBtn:not(:disabled) {
  color: var(--brand);
}

.sendBtn:hover:not(:disabled) {
  background: var(--icon-bg-hover);
  color: var(--brand);
}
